/**
 * Reconciliation. The mirror against QuickBooks, one card per month, for one entity.
 *
 * FINANCE.md 6: "Identify discrepancies. Do not silently correct them." The identifying is done
 * in `packages/finance/src/reconcile.ts` from rows of `derived.reconciliation_line`; this screen
 * shows its report. Every count is the API's integer, every `detail` is the API's finished
 * sentence with its amounts already formatted, and the three amounts a card shows go through
 * `formatMoney` and nothing else (CLAUDE.md 3.1). The one thing this file rearranges is order —
 * the report is oldest first and a person wants the latest month at the top.
 *
 * The bank section is rendered even though it holds nothing: no bank data exists until Plaid
 * (Phase 5), and an empty section is honest where a missing one is a gap nobody sees.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  type Difference,
  type MonthPayout,
  type MonthReport,
  type ReconciliationResponse,
} from '../../api.js';
import { EntityPicker, useEntities, useEntityId } from '../../components/EntityPicker.js';
import { formatMoney } from '../../money.js';

const MONTHS = 6;

/** The label a person reads for each `Difference.kind`; the kind itself is shown beside it. */
const KIND_LABEL: Record<Difference['kind'], string> = {
  documents: 'Documents',
  amounts: 'Amounts',
  drift: 'Drift',
  duplicates: 'Duplicate candidates',
  ambiguous_transfers: 'Ambiguous transfers',
  unsupported_transfers: 'Unconfirmed transfers',
  unresolved_anomalies: 'Unresolved anomalies',
  payouts: 'Stripe payouts',
  anomalies: 'Anomaly candidates',
};

/** The chip a payout's `derived.payout_match` status gets. The status is the verdict. */
const PAYOUT_CHIP: Record<MonthPayout['status'], string> = {
  matched: 'chip chip-ok',
  ambiguous: 'chip chip-warn',
  unmatched: 'chip chip-attention',
};

export function ReconciliationPage(): ReactNode {
  const entityId = useEntityId();
  const entities = useEntities();

  const report = useQuery({
    queryKey: ['finance', 'reconciliation', entityId, MONTHS],
    enabled: entityId !== null,
    queryFn: () =>
      api.get<ReconciliationResponse>(
        `/api/finance/reconciliation?entity=${entityId}&months=${MONTHS}`,
      ),
  });

  const noEntities = entities.data !== undefined && entities.data.entities.length === 0;
  const data = report.data;
  // Oldest first from the report; newest first on the screen. Reordered, never recomputed.
  const months = [
    ...(data?.entities.find((e) => e.entity_id === entityId)?.months ?? []),
  ].reverse();

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Reconciliation</h1>
          <p className="subtitle">
            Documents and amounts in the mirror against QuickBooks, month by month. Code identifies;
            it never corrects.
          </p>
        </div>
      </header>

      <div className="filters">
        <EntityPicker />
      </div>

      {(report.isLoading || (entityId === null && entities.isLoading)) && (
        <p className="muted">Loading…</p>
      )}
      {report.isError && <p className="error">Could not load the report: {report.error.message}</p>}

      {noEntities && (
        <div className="card empty">
          <h2>No entities.</h2>
          <p>
            Run <code>pnpm seed</code> to load the fictional sample portfolio.
          </p>
        </div>
      )}

      {data && (
        <>
          <section className="card recon-bank">
            <h2>Bank vs ledger</h2>
            {data.bank.status === 'not_connected' && (
              <p className="muted">
                No bank feed connected — bank vs ledger arrives with Plaid (Phase 5).
              </p>
            )}
          </section>

          {data.orphan_anomalies > 0 && (
            <div className="alert alert-warn">
              {data.orphan_anomalies}{' '}
              {data.orphan_anomalies === 1 ? 'unresolved anomaly' : 'unresolved anomalies'} with
              neither a transaction nor an entity, so no month of any entity can carry{' '}
              {data.orphan_anomalies === 1 ? 'it' : 'them'}. Counted here rather than lost.
            </div>
          )}

          {months.length === 0 ? (
            <div className="card empty">
              <h2>No months to reconcile.</h2>
              <p>
                Nothing in the mirror for this entity yet. Lines arrive from the QuickBooks
                connector — <code>pnpm qbo:sync</code>.
              </p>
            </div>
          ) : (
            months.map((m) => <MonthCard key={m.month} month={m} />)
          )}

          <p className="muted small recon-as-of">
            Last {data.months} months on the view · as of {new Date(data.as_of).toLocaleString()}
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

function MonthCard({ month: m }: { month: MonthReport }): ReactNode {
  const attention = m.differences.filter((d) => d.severity === 'attention').length;
  return (
    <section className="card recon-month">
      <header className="recon-month-head">
        <h2>{m.month.slice(0, 7)}</h2>
        {m.reconciled ? (
          <span className="status status-approved">Reconciled</span>
        ) : attention > 0 ? (
          <span className="status status-conflict">
            {attention} {attention === 1 ? 'needs' : 'need'} attention
          </span>
        ) : (
          <span className="status status-rule">Open items</span>
        )}
      </header>

      <div className="recon-grid">
        <dl className="kv">
          <dt>QuickBooks documents</dt>
          <dd>{m.raw_documents}</dd>
          <dt>Mirror documents</dt>
          <dd>{m.core_documents}</dd>
          <dt>Mirror lines</dt>
          <dd>{m.core_lines}</dd>
          <dt>QuickBooks total</dt>
          <dd className="num">{formatMoney(m.raw_amount_cents)}</dd>
          <dt>Mirror total</dt>
          <dd className="num">{formatMoney(m.core_amount_cents)}</dd>
          <dt>Difference (mirror − QuickBooks)</dt>
          <dd className="num">{formatMoney(m.difference_cents)}</dd>
        </dl>
        <dl className="kv">
          <dt>Classified</dt>
          <dd>{m.classified}</dd>
          <dt>Unclassified</dt>
          <dd>{m.unclassified}</dd>
          <dt>Rule hits</dt>
          <dd>{m.rule_proposals}</dd>
          <dt>Rule conflicts</dt>
          <dd>{m.rule_conflicts}</dd>
          <dt>Duplicate candidates</dt>
          <dd>{m.duplicate_candidates}</dd>
          <dt>Transfers paired / ambiguous</dt>
          <dd>
            {m.paired_transfers} / {m.ambiguous_transfers}
          </dd>
          <dt>Unresolved anomalies</dt>
          <dd>{m.unresolved_anomalies}</dd>
          <dt>Drift</dt>
          <dd>{m.drift}</dd>
          <dt>Payouts unmatched / ambiguous</dt>
          <dd>
            {m.payouts_unmatched} / {m.payouts_ambiguous}
          </dd>
          <dt>Anomaly candidates (amount / vendor / subscription)</dt>
          <dd>
            {m.anomaly_unusual_amount} / {m.anomaly_new_vendor} / {m.anomaly_subscription}
          </dd>
        </dl>
      </div>

      {m.payouts.length > 0 && (
        <div className="recon-payouts">
          <h3>Stripe payouts</h3>
          <ul>
            {m.payouts.map((p) => (
              <li key={p.payout_id}>
                <span className="nowrap">{p.arrival_date}</span>
                <span className="num">{formatMoney(p.amount_cents)}</span>
                <span className={PAYOUT_CHIP[p.status]}>{p.status}</span>
                <span className="recon-payout-detail">
                  <code>{p.stripe_payout_id}</code>
                  {p.deposit_source_record_id !== null && (
                    <>
                      {' · deposit '}
                      <code>{p.deposit_source_record_id}</code>
                    </>
                  )}
                  {p.days_unmatched !== null && (
                    <>
                      {' · '}
                      {p.days_unmatched} {p.days_unmatched === 1 ? 'day' : 'days'}
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {m.differences.length > 0 && (
        <ul className="recon-differences">
          {m.differences.map((d) => (
            <li key={d.kind}>
              <span className={d.severity === 'attention' ? 'chip chip-attention' : 'chip'}>
                {d.severity}
              </span>
              <span className="recon-kind">
                {KIND_LABEL[d.kind]} <code>{d.kind}</code>
              </span>
              <span>{d.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
